// Zip Code API Integration
// Uses free USDA PHZM API for accurate hardiness zone data

import { ZipCodeData, addZipCode } from './zipCodeDatabase'

// USDA PHZM API - Free, no key required
const USDA_API_BASE = 'https://phzmapi.org'

// Open-Meteo API - Free, no key required  
const OPEN_METEO_BASE = 'https://archive-api.open-meteo.com/v1'

export interface FrostDateResult {
  zipCode: string
  zone: string
  lastFrost: string // MM-DD
  firstFrost: string // MM-DD
  source: 'usda' | 'open-meteo' | 'estimate'
  confidence: 'high' | 'medium' | 'low'
}

// Fetch from USDA PHZM API (free, official)
export async function fetchFromUSDA(zipCode: string): Promise<FrostDateResult | null> {
  try {
    // USDA API endpoint
    const response = await fetch(`${USDA_API_BASE}/${zipCode}.json`)
    
    if (!response.ok) {
      console.log(`USDA API: No data for ${zipCode}`)
      return null
    }
    
    const data = await response.json()
    
    // USDA returns zone but not frost dates directly
    // We map zone to typical frost dates
    const zone = data.zone
    const frostDates = getTypicalFrostDatesForZone(zone)
    
    if (!frostDates) {
      return null
    }
    
    return {
      zipCode,
      zone,
      lastFrost: frostDates.lastFrost,
      firstFrost: frostDates.firstFrost,
      source: 'usda',
      confidence: 'high'
    }
  } catch (error) {
    console.error('USDA API error:', error)
    return null
  }
}

// Fetch historical climate data from Open-Meteo (free)
export async function fetchFromOpenMeteo(
  zipCode: string, 
  lat: number, 
  lon: number
): Promise<FrostDateResult | null> {
  try {
    // Get historical temperature data for the last 10 years
    const endDate = new Date()
    const startDate = new Date()
    startDate.setFullYear(endDate.getFullYear() - 10)
    
    const params = new URLSearchParams({
      latitude: lat.toString(),
      longitude: lon.toString(),
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0],
      daily: 'temperature_2m_min',
      timezone: 'auto'
    })
    
    const response = await fetch(`${OPEN_METEO_BASE}/archive?${params}`)
    
    if (!response.ok) {
      console.log(`Open-Meteo API: No data for ${zipCode}`)
      return null
    }
    
    const data = await response.json()
    
    // Calculate frost dates from historical data
    // Frost = minimum temp ≤ 32°F (0°C)
    const frostDates = calculateFrostDatesFromHistory(data.daily)
    
    if (!frostDates) {
      return null
    }
    
    // Determine zone from frost dates
    const zone = determineZoneFromFrost(frostDates.lastFrost)
    
    return {
      zipCode,
      zone,
      lastFrost: frostDates.lastFrost,
      firstFrost: frostDates.firstFrost,
      source: 'open-meteo',
      confidence: 'medium'
    }
  } catch (error) {
    console.error('Open-Meteo API error:', error)
    return null
  }
}

// Get coordinates from zip code (using free geocoding)
export async function getCoordinates(zipCode: string): Promise<{ lat: number; lon: number } | null> {
  try {
    // Using Nominatim (OpenStreetMap) - free, no key
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?postalcode=${zipCode}&country=USA&format=json&limit=1`,
      {
        headers: {
          'User-Agent': 'GardenPlanner/1.0'
        }
      }
    )
    
    if (!response.ok) return null
    
    const data = await response.json()
    if (data.length === 0) return null
    
    return {
      lat: parseFloat(data[0].lat),
      lon: parseFloat(data[0].lon)
    }
  } catch (error) {
    console.error('Geocoding error:', error)
    return null
  }
}

// Main function to get frost dates - tries multiple sources
export async function getFrostDates(zipCode: string): Promise<FrostDateResult | null> {
  // Try 1: USDA API (most accurate, official)
  const usdaResult = await fetchFromUSDA(zipCode)
  if (usdaResult) {
    // Save to database
    addZipCode({
      zipCode: usdaResult.zipCode,
      zone: usdaResult.zone,
      lastFrost: usdaResult.lastFrost,
      firstFrost: usdaResult.firstFrost,
      source: 'database',
      lastUpdated: new Date().toISOString().split('T')[0]
    })
    return usdaResult
  }
  
  // Try 2: Open-Meteo with coordinates
  const coords = await getCoordinates(zipCode)
  if (coords) {
    const openMeteoResult = await fetchFromOpenMeteo(zipCode, coords.lat, coords.lon)
    if (openMeteoResult) {
      // Save to database
      addZipCode({
        zipCode: openMeteoResult.zipCode,
        zone: openMeteoResult.zone,
        lastFrost: openMeteoResult.lastFrost,
        firstFrost: openMeteoResult.firstFrost,
        source: 'ai',
        lastUpdated: new Date().toISOString().split('T')[0]
      })
      return openMeteoResult
    }
  }
  
  // Try 3: Pattern-based estimation (fallback)
  return estimateFromPattern(zipCode)
}

// Helper: Get typical frost dates for a zone
function getTypicalFrostDatesForZone(zone: string): { lastFrost: string; firstFrost: string } | null {
  // USDA Zone to typical frost dates
  const zoneDates: Record<string, { lastFrost: string; firstFrost: string }> = {
    '3a': { lastFrost: '05-15', firstFrost: '09-15' },
    '3b': { lastFrost: '05-10', firstFrost: '09-20' },
    '4a': { lastFrost: '05-05', firstFrost: '09-25' },
    '4b': { lastFrost: '05-01', firstFrost: '10-01' },
    '5a': { lastFrost: '04-25', firstFrost: '10-05' },
    '5b': { lastFrost: '04-20', firstFrost: '10-10' },
    '6a': { lastFrost: '04-15', firstFrost: '10-15' },
    '6b': { lastFrost: '04-10', firstFrost: '10-20' },
    '7a': { lastFrost: '04-05', firstFrost: '10-25' },
    '7b': { lastFrost: '04-01', firstFrost: '10-31' },
    '8a': { lastFrost: '03-25', firstFrost: '11-05' },
    '8b': { lastFrost: '03-15', firstFrost: '11-15' },
    '9a': { lastFrost: '03-01', firstFrost: '11-25' },
    '9b': { lastFrost: '02-15', firstFrost: '12-01' },
    '10a': { lastFrost: '02-01', firstFrost: '12-15' },
    '10b': { lastFrost: '01-15', firstFrost: '12-31' },
  }
  
  return zoneDates[zone] || zoneDates[zone.replace(/[ab]$/, '')] || null
}

// Helper: Calculate frost dates from historical weather data
function calculateFrostDatesFromHistory(daily: any): { lastFrost: string; firstFrost: string } | null {
  if (!daily || !daily.time || !daily.temperature_2m_min) {
    return null
  }
  
  const dates: Date[] = daily.time.map((t: string) => new Date(t))
  const temps: number[] = daily.temperature_2m_min
  
  // Find last spring frost (after March 1)
  let lastSpringFrost: Date | null = null
  for (let i = 0; i < dates.length; i++) {
    if (dates[i].getMonth() >= 2 && dates[i].getMonth() <= 4) { // March-May
      if (temps[i] <= 0) { // 0°C = 32°F
        lastSpringFrost = dates[i]
      }
    }
  }
  
  // Find first fall frost (before Nov 30)
  let firstFallFrost: Date | null = null
  for (let i = dates.length - 1; i >= 0; i--) {
    if (dates[i].getMonth() >= 9 && dates[i].getMonth() <= 10) { // Oct-Nov
      if (temps[i] <= 0) {
        firstFallFrost = dates[i]
        break
      }
    }
  }
  
  if (!lastSpringFrost || !firstFallFrost) {
    return null
  }
  
  return {
    lastFrost: formatDate(lastSpringFrost),
    firstFrost: formatDate(firstFallFrost)
  }
}

// Helper: Determine zone from frost date
function determineZoneFromFrost(lastFrost: string): string {
  const [month, day] = lastFrost.split('-').map(Number)
  const dayOfYear = month * 30 + day
  
  if (dayOfYear < 45) return '10b'
  if (dayOfYear < 60) return '10a'
  if (dayOfYear < 75) return '9b'
  if (dayOfYear < 90) return '9a'
  if (dayOfYear < 100) return '8b'
  if (dayOfYear < 110) return '8a'
  if (dayOfYear < 120) return '7b'
  if (dayOfYear < 130) return '7a'
  if (dayOfYear < 140) return '6b'
  if (dayOfYear < 150) return '6a'
  if (dayOfYear < 160) return '5b'
  if (dayOfYear < 170) return '5a'
  if (dayOfYear < 180) return '4b'
  return '4a'
}

// Helper: Pattern-based estimation (fallback)
function estimateFromPattern(zipCode: string): FrostDateResult | null {
  const firstDigit = zipCode[0]
  
  const patterns: Record<string, { zone: string; lastFrost: string; firstFrost: string }> = {
    '0': { zone: '6b', lastFrost: '04-15', firstFrost: '10-15' },
    '1': { zone: '6a', lastFrost: '04-25', firstFrost: '10-10' },
    '2': { zone: '7b', lastFrost: '04-05', firstFrost: '10-25' },
    '3': { zone: '8b', lastFrost: '03-15', firstFrost: '11-15' },
    '4': { zone: '6b', lastFrost: '04-15', firstFrost: '10-15' },
    '5': { zone: '6b', lastFrost: '04-15', firstFrost: '10-15' },
    '6': { zone: '6a', lastFrost: '04-25', firstFrost: '10-10' },
    '7': { zone: '8a', lastFrost: '03-25', firstFrost: '11-10' },
    '8': { zone: '9b', lastFrost: '02-15', firstFrost: '12-01' },
    '9': { zone: '9b', lastFrost: '02-15', firstFrost: '12-01' },
  }
  
  const pattern = patterns[firstDigit]
  if (!pattern) return null
  
  return {
    zipCode,
    zone: pattern.zone,
    lastFrost: pattern.lastFrost,
    firstFrost: pattern.firstFrost,
    source: 'estimate',
    confidence: 'low'
  }
}

// Helper: Format date as MM-DD
function formatDate(date: Date): string {
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  return `${month}-${day}`
}

// Test function
export async function testAPIs(): Promise<void> {
  console.log('Testing Zip Code APIs...')
  
  // Test USDA API
  const usdaResult = await fetchFromUSDA('90210')
  console.log('USDA Result:', usdaResult)
  
  // Test geocoding
  const coords = await getCoordinates('90210')
  console.log('Coordinates:', coords)
  
  // Test full flow
  const fullResult = await getFrostDates('90210')
  console.log('Full Result:', fullResult)
}
